import classNames from 'classnames';
import { Alert, ConfigProvider, Input, InputRef } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Routes } from '@/routes';
import { Flex } from '@/components/common';
import { useUserContext } from '@/context';
import ServicesStatus from '../ServicesStatus';
import useKeyboardShortcut from 'use-keyboard-shortcut';

enum Colors {
  Route = '#1abc9c',
  Action = '#9b59b6',
  RedAction = '#e74c3c',
  // blue: '#3498db'
}

enum Types {
  Route = 'Route',
  Action = 'Action',
  RedAction = 'RedAction'
}

enum Modes {
  Init = 'init',
  ServicesStatus = 'services-status'
}

export default function DevToolContent({ hide }) {
  const [mode, setMode] = useState<Modes>(Modes.Init);
  const [selected, setSelected] = useState(0);
  const [search, setSearch] = useState('');
  const inputRef = useRef<InputRef>();
  const { logout } = useUserContext();
  const navigate = useNavigate();

  const items = useMemo(() => [
    { name: 'Home', type: Types.Route, action: () => navigate(Routes.Home) },
    { name: 'Reserve', type: Types.Route, action: () => navigate(Routes.FoodReserve) },
    { name: 'Reserve Report', type: Types.Route, action: () => navigate(Routes.FoodReservesReport) },
    { name: 'Activity Logs', type: Types.Route, action: () => navigate(Routes.UserActivityReport) },
    { name: 'Daily Sale', type: Types.Route, action: () => navigate(Routes.FoodDailySale) },
    { name: 'Messages', type: Types.Route, action: () => navigate(Routes.UserMessages) },
    { name: 'Profile', type: Types.Route, action: () => navigate(Routes.UserProfile) },
    { name: 'Change Password', type: Types.Route, action: () => navigate(`${Routes.UserProfile}?scope=password`) },
    { name: 'Run Services Status', type: Types.Action, action: () => {
      setMode(Modes.ServicesStatus); return true;
    } },
    { name: 'Logout', type: Types.RedAction, action: () => logout() }
  ], []);

  const filteredItems = items.filter(x => x.name.toLowerCase().includes(search.toLowerCase()));

  const executeItem = useCallback((item: typeof items[0]) => {
    if (item.action) {
      if ((item.action() as any) !== true)
        hide();
    }
  }, [hide]);

  const executeCurrentItem = useCallback(() => {
    if (filteredItems.length > 0) {
      executeItem(filteredItems[selected]);
    }
  }, [executeItem, filteredItems, selected]);

  useKeyboardShortcut(['ArrowUp'], () => {
    setSelected(current => {
      const newValue = current !== 0 ? current - 1 : filteredItems.length - 1;
      return newValue;
    });
  },
  {
    overrideSystem: true,
    ignoreInputFields: false,
    repeatOnHold: true
  }
  );

  useKeyboardShortcut(['ArrowDown'], () => {
    setSelected(current => {
      const newValue = current !== filteredItems.length - 1 ? current + 1 : 0;
      return newValue;
    });
  },
  {
    overrideSystem: true,
    ignoreInputFields: false,
    repeatOnHold: true
  }
  );

  useKeyboardShortcut(['Enter'], executeCurrentItem);

  useEffect(() => {
    if (inputRef?.current) {
      inputRef?.current.focus();
    }
  }, [open, logout, navigate]);

  return (
    <ConfigProvider direction="ltr">
      {mode === Modes.ServicesStatus && <ServicesStatus />}
      {mode === Modes.Init && (
        <Flex className="ltr text-end" direction="column">
          <Input
            autoFocus
            ref={inputRef}
            value={search}
            onPressEnter={executeCurrentItem}
            placeholder="Search for Command"
            onChange={(e) => {
              setSearch(e.target.value);
              setSelected(0);
            }}
          />
          <Flex gap={4} direction="column">
            {filteredItems
              .map((item, index) => (
                <>
                  <Flex
                    key={index}
                    align="center"
                    onClick={executeItem.bind({}, item)}
                    className={classNames('p-2 rounded-lg hover:bg-[#eee] cursor-pointer', { 'bg-[#eee]': selected === index })}
                  >
                    {item.name}
                    <div
                      style={{ backgroundColor: Colors[item.type], width: '85px' }}
                      className="p-1.5 ms-auto rounded-lg text-white text-center text-xs"
                    >
                      <b>{item.type}</b>
                    </div>
                  </Flex>
                </>
              ))}
            {filteredItems.length === 0 && (
              <Alert type="warning" message="There is no command with this pattern" />
            )}
          </Flex>
        </Flex>
      )}
    </ConfigProvider>
  );
}
